import { NestedMunicipality } from "@habity/types";
import slugify from "slugify";
import { ROUTES } from "@habity/constants";

/**
 * Generate a slug for a municipality
 * @param municipality The municipality object
 * @returns A slugified string for the municipality
 */
export const getMunicipalitySlug = (municipality: Pick<NestedMunicipality, "id" | "name">) => {
  const { id, name } = municipality || {};
  if (!id || !name) return "";

  const nameText = slugify(name, { lower: true });

  return `${nameText}-${id}`;
};

/**
 * Get the path to a municipality's profile page
 * @param municipality The municipality object
 * @param searchParams Optional search parameters to add to the URL
 * @returns The path to the municipality's profile page
 */
export const getMunicipalityPath = (municipality: Pick<NestedMunicipality, "id" | "name">, searchParams?: Record<string, string | number | boolean>) => {
  if (!municipality || !municipality.id || !municipality.name)
    return "/municipalities";
  
  const slug = getMunicipalitySlug(municipality);
  const basePath = `${ROUTES.PROFILES.MUNICIPALITIES}${slug}`;
  
  // If search params are provided, add them to the URL
  if (searchParams && Object.keys(searchParams).length > 0) {
    const urlParams = new URLSearchParams();
    
    // Add each parameter to the URL
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        urlParams.set(key, String(value));
      }
    });
    
    // Return the path with search parameters
    return `${basePath}?${urlParams.toString()}`;
  }
  
  return basePath;
};

export const getQueryFromMunicipality = (municipality: NestedMunicipality) => {
  if (!municipality) {
    return {};
  }

  return {
    municipality_id: municipality.id,
    municipality_name: municipality.name,
  };
};

/**
 * Get the image URL for a municipality
 * @param municipality The municipality object
 * @returns The image URL for the municipality
 */
export const getMunicipalityImageUrl = (municipality: NestedMunicipality) => {  
  // Check if the municipality has an image_url
  if (municipality.image_url) {
    return municipality.image_url;
  }
  
  // Fallback to a placeholder image based on the municipality name
  return `https://source.unsplash.com/random/800x600/?city,${municipality.name.replace(/\s+/g, '-') || 'municipality'}`;
};


export const isActiveMunicipality = (homesCount: number, brokersCount: number) => {
  return homesCount > 0 || brokersCount > 0;
};